/**
 * Course tile badges — data-driven, not position-driven.
 *
 * Before this the catalog badged `i === 0` as "New" forever (the first row of
 * whatever the current sort produced) and called `products.featured`
 * "Bestseller", which claimed sales volume nobody had measured. Both were
 * dishonest in the literal sense: the label asserted a fact the data did not
 * support. Now "New" means genuinely recent and "Featured" says exactly what it
 * is — an admin's pick.
 *
 * There is deliberately NO bestseller badge. Ranking a few dozen courses by
 * enrolment would crown a "bestseller" at three students; the `popular` sort
 * plus a visible student count carries that signal honestly instead.
 */
export const COURSE_NEW_BADGE_DAYS = 30;

export type CourseBadge = 'new' | 'featured';

/** Was the course published inside the recency window? */
export function isNewCourse(
  createdAt: string | Date | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!createdAt) return false;
  const created = createdAt instanceof Date ? createdAt : new Date(createdAt);
  const ms = created.getTime();
  if (Number.isNaN(ms)) return false;
  const ageDays = (now.getTime() - ms) / 86_400_000;
  return ageDays >= 0 && ageDays < COURSE_NEW_BADGE_DAYS;
}

/**
 * At most ONE badge per tile — two competing ribbons on one cover read as
 * noise. Featured wins: it is a deliberate editorial choice, where "new" is
 * merely a timestamp.
 */
export function courseBadge(
  course: { featured?: boolean | null; createdAt?: string | Date | null },
  now: Date = new Date(),
): CourseBadge | null {
  if (course.featured) return 'featured';
  return isNewCourse(course.createdAt, now) ? 'new' : null;
}
